import { filesize } from 'filesize';
import path from 'path-browserify';

import type { UploaderChunkState, UploadKindType } from './state';
import type { StorageMethod, UploadEntity } from './types';

export class UploadError extends Error {}

export class MultipartUploadChunkError extends UploadError {
  chunk: UploaderChunkState<unknown>;

  cause?: Error;

  constructor(chunk: UploaderChunkState<unknown>, cause?: Error) {
    const message = cause?.message ? cause.message : 'Unknown error';
    super(`Chunk ${chunk.index} failed: ${message}`);
    this.chunk = chunk;
    this.cause = cause;
  }
}

export class UninitializedChunksError extends UploadError {
  constructor() {
    super('Chunks are not initialized');
  }
}

export class UninitializedChunkError extends UploadError {
  chunkIndex: number;

  constructor(chunkIndex: number) {
    super(`Chunk ${chunkIndex} is not initialized`);
    this.chunkIndex = chunkIndex;
  }
}

export class UninitializedChunkMetaError extends UploadError {
  chunk: UploaderChunkState<unknown>;

  constructor(chunk: UploaderChunkState<unknown>) {
    super(`Chunk ${chunk.index} meta is not initialized`);
    this.chunk = chunk;
  }
}

export class UninitializedStreamReaderError extends UploadError {}

export class StreamReaderChunkError extends UploadError {}

export class UninitializedChunkObjectUrlError extends UploadError {
  chunk: UploaderChunkState<unknown>;

  constructor(chunk: UploaderChunkState<unknown>) {
    super(`Chunk ${chunk.index} URL object is not initialized`);
    this.chunk = chunk;
  }
}

export class FileSizeError extends UploadError {
  size: number;

  constructor(size: number) {
    super(`File size is too big, max size is ${filesize(size)}`);
    this.size = size;
  }
}

export class InvalidUploadObjectError extends UploadError {
  uploadObject: unknown;

  constructor(uploadObject: unknown, message?: string) {
    super(message ?? 'Invalid upload object');
    this.uploadObject = uploadObject;
  }
}

/**
 * Thrown when an uploader's validate() gate rejects the current state without
 * raising a more specific error of its own.
 */
export class UnsupportedUploadError extends UploadError {
  constructor(message?: string) {
    super(message ?? 'Upload is not supported by this uploader');
  }
}

/**
 * The storage supports uploading, but not this kind of upload: B2 has no proxy
 * endpoints at all, for instance.
 */
export class UnsupportedUploadKindError extends UploadError {
  storageMethod: StorageMethod;

  uploadKind: UploadKindType;

  constructor(storageMethod: StorageMethod, uploadKind: UploadKindType) {
    super(
      `Uploading a ${uploadKind} to ${storageMethod} storage is not supported`,
    );
    this.storageMethod = storageMethod;
    this.uploadKind = uploadKind;
  }
}

export class UnsupportedStorageMethod extends UploadError {
  storageId: string;

  storageMethod: string;

  constructor(storageId: string, storageMethod: string) {
    super(
      `Unsupported storage method ${storageMethod} for storage ${storageId}`,
    );
    this.storageId = storageId;
    this.storageMethod = storageMethod;
  }
}

export class NoStorageSelectedError extends UploadError {
  constructor() {
    super('No storage selected');
  }
}

export class InvalidFileObject extends UploadError {
  fileObject: UploadEntity;

  constructor(fileObject: UploadEntity, message?: string) {
    super(message);
    this.fileObject = fileObject;
  }
}

export class CreateCollectionError extends UploadError {
  cause: Error;

  constructor(cause: Error) {
    super(cause.message);
    this.cause = cause;
  }
}

export class EmptyAssetIdError extends UploadError {}

export class EmptyFileIdError extends UploadError {}

/** The entity the bytes go into has not been created yet. */
export class EmptyUploadEntityIdError extends UploadError {}

export class EmptyMetaError extends UploadError {}

export class CloseFileError extends UploadError {
  cause: Error;

  constructor(cause: Error) {
    super(`Close file failed: ${cause.message}`);
    this.cause = cause;
  }
}

export class CloseProxyError extends UploadError {
  cause: Error;

  constructor(cause: Error) {
    super(`Close proxy failed: ${cause.message}`);
    this.cause = cause;
  }
}

export class CreateProxyError extends UploadError {
  cause: Error;

  constructor(cause: Error) {
    super(`Create proxy failed: ${cause.message}`);
    this.cause = cause;
  }
}

export class FileExistsError extends UploadError {
  constructor(directoryPath: string, fileName: string) {
    const fullPath = path.join(directoryPath, fileName);
    super(`File already exists at path ${fullPath}`);
  }
}

export class CancelUploadError extends UploadError {
  cause: Error;

  constructor(cause: Error) {
    super(`Cancel failed: ${cause.message}`);
    this.cause = cause;
  }
}

export class GenerateKeyframesError extends UploadError {
  cause: Error;

  constructor(cause: Error) {
    super(`Generate keyframes failed: ${cause.message}`);
    this.cause = cause;
  }
}

export class RegisterSubtitlesError extends UploadError {
  cause: Error;

  constructor(cause: Error) {
    super(`Registering subtitles failed: ${cause.message}`);
    this.cause = cause;
  }
}

/**
 * Raised when an upload has to send the file as one request body and the
 * platform cannot make one out of the source it was given.
 */
export class WholeBodyUnsupportedError extends UploadError {
  constructor(sourceType: string) {
    super(
      `Uploading the whole file in one request is not supported for a "${sourceType}" source here`,
    );
  }
}

/**
 * Not an error actually, used to indicate that the upload is aborted normally
 * e.g. by cancelling or pausing.
 */
export class AbortUploadError extends UploadError {}

export class FailedUploadError extends UploadError {
  cause: unknown;

  constructor(cause: unknown) {
    super(cause instanceof Error ? cause.message : 'Upload failed');
    this.cause = cause;
  }
}
